import type { ListingList } from "../post/types";
import { ListingType } from "../../types/userListing";
import { isListingAllowedForCaptionedVideo, isNicheVideoListing, listingSelectValue, normalizeListingType, withNicheVideoEntry } from "../misc/custom-posts/adhocListingTypes";
import type { CardSlug } from "./rules/deepLinks";

/** The five cards that open the ad hoc dialog. Open House and Video Showcase take the listing dialogs instead. */
export type PresetCard = "my-brand" | "listing" | "captioned" | "niche" | "lead-gen";
export interface AddPostPreset { card: PresetCard; campaignId?: string }

const PRESET_CARDS: readonly CardSlug[] = ["my-brand", "listing", "captioned", "niche", "lead-gen"];
export const isPresetCard = (slug: CardSlug): slug is PresetCard => PRESET_CARDS.includes(slug);

const TITLES: Record<PresetCard | "open-house" | "video-showcase", string> = {
    "my-brand": "Schedule My Brand Posts",
    listing: "Schedule Listing Posts",
    captioned: "Schedule Captioned Video Posts",
    niche: "Schedule My Niche Video",
    "lead-gen": "Schedule Lead Generation Posts",
    "open-house": "Schedule Open House Posts",
    "video-showcase": "Schedule Video Showcase Posts",
};
export const presetDialogTitle = (card: keyof typeof TITLES): string => TITLES[card];

export const presetStartsCaptioned = (preset: AddPostPreset): boolean => preset.card === "captioned" || preset.card === "niche";

/**
 * A paused campaign still takes an ad hoc Listing post, but not a captioned, open house or video showcase
 * post; those cards use this. A row without the status (an older api) counts as ACTIVE.
 */
export const isPostableRow = (r: ListingList): boolean => r.campaignStatus === undefined || r.campaignStatus === "ACTIVE";
export const isPropertyRow = (r: ListingList): boolean => ["mlslisting", "rentcastlisting"].includes(normalizeListingType(r.listingType));

/** The campaign dropdown's rows for a card; empty = the dropdown is hidden (the card picks for the user). */
export function rowsForPreset(rows: ListingList[], preset: AddPostPreset): ListingList[] {
    switch (preset.card) {
        // Every property campaign, paused ones included: a Listing post still goes out on a paused campaign.
        case "listing":
            return rows.filter(isPropertyRow);
        // Today's captioned filter minus the My Niche Video entry (that card exists on its own) and minus
        // paused campaigns (a captioned video does not publish there).
        case "captioned":
            return rows.filter((r) => isListingAllowedForCaptionedVideo(r.listingType) && !isNicheVideoListing(r.listingType) && isPostableRow(r));
        default:
            return [];
    }
}

/** The row the card picks for the user, if any; the deep-linked campaign when it is in the card's rows. */
export function presetPick(rows: ListingList[], preset: AddPostPreset, nicheEligible: boolean): ListingList | undefined {
    switch (preset.card) {
        case "my-brand":
            return rows.find((r) => normalizeListingType(r.listingType) === "mybrand");
        case "lead-gen":
            return rows.find((r) => normalizeListingType(r.listingType) === "leadgeneration");
        case "niche":
            return withNicheVideoEntry(rows, nicheEligible).find((r) => r.listingType === ListingType.NicheVideo && isPostableRow(r));
        default: {
            if (!preset.campaignId) return undefined;
            return rowsForPreset(rows, preset).find((r) => r.campaignId === preset.campaignId);
        }
    }
}

/** The dialog holds nothing, or only the row the card picked itself: a first step the user has not touched. */
export function isCardOwnPick(selected: ListingList | undefined, rows: ListingList[], preset: AddPostPreset, nicheEligible: boolean): boolean {
    return listingSelectValue(selected) === listingSelectValue(presetPick(rows, preset, nicheEligible));
}

/** The card would have a row or a pick if its paused campaigns counted: the dialog says "paused", not "missing". */
export function presetBlockedByPause(rows: ListingList[], preset: AddPostPreset, nicheEligible: boolean): boolean {
    const offers = (rs: ListingList[]): boolean => rowsForPreset(rs, preset).length > 0 || !!presetPick(rs, preset, nicheEligible);
    return !offers(rows) && offers(rows.map((r) => ({ ...r, campaignStatus: "ACTIVE" })));
}
